// `model/inp/thematicAttributes.ts` imports back from `model/results/simulationResults.ts`
// — a real bidirectional coupling with `model/inp`, not a literal file cycle
// today (deep-review dependency-mapper finding, Medium). See that file's own
// matching comment.
import type { ElementRef } from "../inp/properties.ts";
import type { TextKey } from "../../i18n/en.ts";
import { unitSystemOf } from "../units.ts";
import type { SimulationResults } from "./simulationResults.ts";

export type ResultVariableKey =
  | "depth"
  | "head"
  | "volume"
  | "totalInflow"
  | "flooding"
  | "flow"
  | "velocity"
  | "capacity"
  | "runoff"
  | "gwFlow"
  | "precip"
  | "evap"
  | "infil";

interface ResultVariableDef {
  key: ResultVariableKey;
  labelKey: TextKey;
  map: (results: SimulationResults) => Map<string, number[]>;
  values: (results: SimulationResults, name: string) => number[] | undefined;
}

function variable(
  key: ResultVariableKey,
  labelKey: TextKey,
  map: (results: SimulationResults) => Map<string, number[]>
): ResultVariableDef {
  return { key, labelKey, map, values: (r, n) => map(r).get(n) };
}

const NODE_VARIABLES: ResultVariableDef[] = [
  variable("depth", "results.depth", (r) => r.nodeDepth),
  variable("head", "results.head", (r) => r.nodeHead),
  variable("volume", "results.volume", (r) => r.nodeVolume),
  variable("totalInflow", "results.totalInflow", (r) => r.nodeTotalInflow),
  variable("flooding", "results.flooding", (r) => r.nodeFlooding),
];

const LINK_VARIABLES: ResultVariableDef[] = [
  variable("flow", "results.flow", (r) => r.linkFlow),
  variable("velocity", "results.velocity", (r) => r.linkVelocity),
  variable("capacity", "results.capacity", (r) => r.linkCapacity),
];

const SUBCATCHMENT_VARIABLES: ResultVariableDef[] = [
  variable("runoff", "results.runoff", (r) => r.subcatchmentRunoff),
  variable("gwFlow", "results.gwFlow", (r) => r.subcatchmentGwFlow),
  variable("precip", "results.precipitation", (r) => r.subcatchmentPrecip),
  variable("evap", "results.evaporation", (r) => r.subcatchmentEvap),
  variable("infil", "results.infiltration", (r) => r.subcatchmentInfil),
];

/** `[]` for a raingage, which has no simulation output of its own. */
export function variablesFor(category: ElementRef["category"]): ResultVariableDef[] {
  if (category === "node") return NODE_VARIABLES;
  if (category === "link") return LINK_VARIABLES;
  if (category === "subcatchment") return SUBCATCHMENT_VARIABLES;
  return [];
}

/** `null` when the variable doesn't apply to this category, or this element has no series of its own (e.g. a link this port couldn't model). */
export function seriesFor(
  results: SimulationResults,
  ref: ElementRef,
  variableKey: ResultVariableKey
): { labelKey: TextKey; values: number[] } | null {
  const def = variablesFor(ref.category).find((v) => v.key === variableKey);
  if (!def) return null;
  const values = def.values(results, ref.name);
  return values ? { labelKey: def.labelKey, values } : null;
}

/** `undefined` when `variableKey` doesn't belong to `category` at all — a caller bug, not a real "no data" case (every variable that does belong always resolves to a real field, just possibly all-zero). */
export function seriesMapFor(
  results: SimulationResults,
  category: ElementRef["category"],
  variableKey: ResultVariableKey
): Map<string, number[]> | undefined {
  return variablesFor(category)
    .find((v) => v.key === variableKey)
    ?.map(results);
}

/**
 * `""` means "don't show a unit suffix" — Capacity is a dimensionless ratio
 * with no unit at all. Deliberately a separate table from
 * `model/inp/properties.ts`'s own `formatUnit` — see that function's own
 * header for why (different key domain, different unit set, deliberate flow
 * unit token casing difference).
 */
export function unitLabelFor(variableKey: ResultVariableKey, flowUnits: string): string {
  const lengthUnit = unitSystemOf(flowUnits) === "US" ? "ft" : "m";
  // Precip/Evap/Infil use the project's own rain-depth unit (in/mm), a
  // genuinely different base unit than lengthUnit (ft/m).
  const rainUnit = unitSystemOf(flowUnits) === "US" ? "in" : "mm";
  switch (variableKey) {
    case "depth":
    case "head":
      return lengthUnit;
    case "volume":
      return lengthUnit === "ft" ? "ft³" : "m³";
    case "velocity":
      return `${lengthUnit}/s`;
    case "capacity":
      return "";
    case "precip":
    case "infil":
      return `${rainUnit}/hr`;
    case "evap":
      return `${rainUnit}/day`; // A genuinely different time basis than precip/infil's own per-hour rate.
    default:
      return flowUnits.toLowerCase(); // totalInflow, flooding, flow, runoff, gwFlow
  }
}
